import i18next from 'i18next';
import { ExternalProductData, ExtProdSortBy, ExtProductCategory, ExtProductFreq, ExtProductUsage } from './model';
import { compareSqlDates } from 'helpers/helpers';

export function sortExtProducts(
  a: Partial<ExternalProductData>,
  b: Partial<ExternalProductData>,
  sortBy: ExtProdSortBy,
  orderDir: boolean,
) {
  const preSort = {
    [ExtProductCategory.EDWARD]: 0,
    [ExtProductCategory.FINANCIAL_PRODUCT]: 1,
    [ExtProductCategory.PROPERTY]: 2,
    [ExtProductCategory.LOAN]: 3,
    [ExtProductCategory.NONE]: 4,
  };

  switch (sortBy) {
    case ExtProdSortBy.TITLE:
      return a.title!.localeCompare(b.title!) * (orderDir ? 1 : -1);
    case ExtProdSortBy.INST:
      const categorySort = preSort[a.category!] - preSort[b.category!];
      if (categorySort === 0) {
        if (a.financialInstitution?.title === b.financialInstitution?.title) {
          return a.title!.localeCompare(b.title!) * (orderDir ? 1 : -1);
        } else {
          return (
            (a.financialInstitution?.title || '').localeCompare(b.financialInstitution?.title || '') *
            (orderDir ? 1 : -1)
          );
        }
      } else {
        return categorySort;
      }
    case ExtProdSortBy.BALANCE:
      return (
        (a.balance!.value * (a.category === ExtProductCategory.LOAN ? -1 : 1) -
          b.balance!.value * (b.category === ExtProductCategory.LOAN ? -1 : 1)) *
        (orderDir ? 1 : -1)
      );
    default:
      return 0;
  }
}

export function sortByTitle(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return (a.title || '').localeCompare(b.title || '');
}

export function sortByType(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return i18next.t(`externalProduct_type_${a.type}`).localeCompare(i18next.t(`externalProduct_type_${b.type}`));
}

export function sortByFinInstTitle(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return (a.financialInstitution?.title || '').localeCompare(b.financialInstitution?.title || '');
}

export function sortByBalance(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return (
    (a.balance?.value || 0) * (a.category === ExtProductCategory.LOAN ? -1 : 1) -
    (b.balance?.value || 0) * (b.category === ExtProductCategory.LOAN ? -1 : 1)
  );
}

export function sortByPeriodic(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return (
    (a.periodic?.value?.value || 0) +
    (a.employer?.value?.value || 0) +
    (a.government?.value?.value || 0) -
    ((b.periodic?.value?.value || 0) + (b.employer?.value?.value || 0) + (b.government?.value?.value || 0))
  );
}

export function sortByInterest(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return (a.interest || 0) - (b.interest || 0);
}

export function sortByLastUpdate(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return (a.lastUpdate || '').localeCompare(b.lastUpdate || '');
}

export function sortByUsage(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return (
    a.usage && a.category !== ExtProductCategory.LOAN
      ? a.frequency === ExtProductFreq.INFINITE
        ? i18next.t(`externalProduct_freq_${ExtProductFreq.INFINITE}`)
        : i18next.t(`externalProduct_usage_${a.usage}`)
      : ''
  ).localeCompare(
    b.usage && b.category !== ExtProductCategory.LOAN
      ? b.frequency === ExtProductFreq.INFINITE
        ? i18next.t(`externalProduct_freq_${ExtProductFreq.INFINITE}`)
        : i18next.t(`externalProduct_usage_${b.usage}`)
      : '',
  );
}

export function sortByFrequency(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  const aF = !a.frequency
    ? ''
    : a.frequency !== ExtProductFreq.INFINITE && a.usage !== ExtProductUsage.NONE
    ? i18next.t(a.frequency)
    : '';
  const bF = !b.frequency
    ? ''
    : b.frequency !== ExtProductFreq.INFINITE && b.usage !== ExtProductUsage.NONE
    ? i18next.t(b.frequency)
    : '';
  return aF.localeCompare(bF);
}

export function sortByUntil(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return compareSqlDates(
    a.frequency === ExtProductFreq.INFINITE || a.usage === ExtProductUsage.NONE
      ? '3000-01-01'
      : a.since || a.periodic?.since || '',
    b.frequency === ExtProductFreq.INFINITE || b.usage === ExtProductUsage.NONE
      ? '3000-01-01'
      : b.since || b.periodic?.since || '',
  );
}

export function sortByAmount(a: Partial<ExternalProductData>, b: Partial<ExternalProductData>) {
  return (a.value?.value || 0) - (b.value?.value || 0);
}
